import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import NavBar from './components/NavBar';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import HomePage from './pages/HomePage';
import Footer from './components/Footer';
import InstallPrompt from './components/InstallPrompt';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ReadingPage from './pages/ReadingPage';
import CircularReadingPage from './pages/CircularReadingPage';
import ConsultationPage from './pages/ConsultationPage';
import ConsultationHistoryPage from './pages/ConsultationHistoryPage';
import ConsultationDetailPage from './pages/ConsultationDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import HistoryPage from './pages/HistoryPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import LoadingSpinner from './components/LoadingSpinner';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import SobreAuroraPage from './pages/SobreAuroraPage';
import EscolhaPage from './pages/EscolhaPage';
import TarotPage from './pages/TarotPage';
import CiganoPage from './pages/CiganoPage';
import SibillaPage from './pages/SibillaPage';
import NumerologiaPage from './pages/NumerologiaPage';
import KabbalahPage from './pages/KabbalahPage';
import JungPage from './pages/JungPage';
import BemEstarPage from './pages/BemEstarPage';
import AmorPage from './pages/AmorPage';
import ProsperidadePage from './pages/ProsperidadePage';
import KarmaPage from './pages/KarmaPage';
import CaminhosPage from './pages/CaminhosPage';
import AstrologiaPage from './pages/AstrologiaPage';
import SinastriaPage from './pages/SinastriaPage';
import RelationshipChartPage from './pages/RelationshipChartPage';
import AuroraQuestionPage from './pages/AuroraQuestionPage';
import HoroscopePage from './pages/HoroscopePage';
import PublicHoroscopePage from './pages/PublicHoroscopePage';
import CompatibilidadeEscolhaPage from './pages/CompatibilidadeEscolhaPage';
import CompatibilidadePage from './pages/CompatibilidadePage';

// Componente para rotas protegidas
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (!user.is_email_verified) return <Navigate to="/verify" />;
  return <>{children}</>;
};

// Componente para rotas de admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Componente para rotas públicas (redireciona se já estiver logado)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return !user ? <>{children}</> : <Navigate to="/dashboard" />;
};

const HIDE_NAV = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify', '/escolha'];
const HIDE_NAV_PREFIXES = ['/admin', '/horoscope/', '/compatibilidade'];
// Páginas públicas/compartilháveis que continuam mostrando a NavBar quando o
// usuário está logado (senão parece que ele foi deslogado ao abrir de dentro do app).
const KEEP_NAV_WHEN_AUTHED_PREFIXES = ['/compatibilidade'];

function NavBarWrapper() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const keepWhenAuthed = user && KEEP_NAV_WHEN_AUTHED_PREFIXES.some(p => pathname.startsWith(p));
  if (!keepWhenAuthed && (HIDE_NAV.includes(pathname) || HIDE_NAV_PREFIXES.some(p => pathname.startsWith(p)))) return null;
  return <NavBar />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Páginas privadas (dados do usuário, admin, checkout, fluxo de pagamento) que os
// buscadores não devem indexar. Marcadas via <meta name="robots"> aqui em vez de
// listadas no robots.txt público — assim o caminho de cada uma não fica exposto
// num "índice de rotas" que qualquer um lê. Some nas páginas públicas pra não
// afetar o SEO da home, horóscopos e institucionais.
const NOINDEX_PREFIXES = [
  '/dashboard', '/profile', '/checkout', '/admin', '/consultation',
  '/history', '/reading', '/payment/', '/sinastria/', '/composto/',
  '/davison/', '/verify', '/reset-password',
];

function RobotsMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const hide = NOINDEX_PREFIXES.some(p => pathname === p || pathname.startsWith(p));
    let tag = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (hide) {
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = 'robots';
        document.head.appendChild(tag);
      }
      tag.content = 'noindex, nofollow';
    } else if (tag) {
      tag.remove();
    }
  }, [pathname]);
  return null;
}

// SPA: o Pixel/GA do index.html só contam a 1ª página. Dispara PageView nas
// navegações seguintes (pula o mount inicial pra não duplicar a 1ª).
function PageViewTracker() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const block = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'IMG') e.preventDefault();
    };
    document.addEventListener('contextmenu', block);
    return () => document.removeEventListener('contextmenu', block);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <PageViewTracker />
          <RobotsMeta />
          <div className="min-h-screen bg-gradient-mystical flex flex-col">
            <NavBarWrapper />
            <Routes>
            {/* Rotas públicas */}
            <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              } 
            />
            
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
            <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
            <Route path="/sobre-aurora" element={<SobreAuroraPage />} />
            <Route path="/escolha" element={<EscolhaPage />} />
            <Route path="/horoscope/:sign" element={<PublicHoroscopePage />} />
            <Route path="/compatibilidade" element={<CompatibilidadeEscolhaPage />} />
            <Route path="/compatibilidade/:signA/:signB" element={<CompatibilidadePage />} />
            <Route path="/tarot" element={<ProtectedRoute><TarotPage /></ProtectedRoute>} />
            <Route path="/baralho-cigano" element={<ProtectedRoute><CiganoPage /></ProtectedRoute>} />
            <Route path="/baralho-sibilla" element={<ProtectedRoute><SibillaPage /></ProtectedRoute>} />
            <Route path="/numerologia" element={<ProtectedRoute><NumerologiaPage /></ProtectedRoute>} />
            <Route path="/kabbalah" element={<ProtectedRoute><KabbalahPage /></ProtectedRoute>} />
            <Route path="/jung" element={<ProtectedRoute><JungPage /></ProtectedRoute>} />
            <Route path="/bem-estar" element={<ProtectedRoute><BemEstarPage /></ProtectedRoute>} />
            <Route path="/amor" element={<ProtectedRoute><AmorPage /></ProtectedRoute>} />
            <Route path="/prosperidade" element={<ProtectedRoute><ProsperidadePage /></ProtectedRoute>} />
            <Route path="/karma" element={<ProtectedRoute><KarmaPage /></ProtectedRoute>} />
            <Route path="/caminhos" element={<ProtectedRoute><CaminhosPage /></ProtectedRoute>} />
            <Route path="/astrologia" element={<ProtectedRoute><AstrologiaPage /></ProtectedRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Rotas protegidas */}
            <Route
              path="/verify"
              element={<VerifyEmailPage />}
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reading" 
              element={
                <ProtectedRoute>
                  <CircularReadingPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/consultation" 
              element={
                <ProtectedRoute>
                  <ConsultationPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout" 
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/consultation-history" 
              element={
                <ProtectedRoute>
                  <ConsultationHistoryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/consultation/:consultationId" 
              element={
                <ProtectedRoute>
                  <ConsultationDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/history" 
              element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Rotas do Admin */}
            <Route 
              path="/admin/login" 
              element={
                <PublicRoute>
                  <AdminLogin />
                </PublicRoute>
              } 
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin/dashboard" replace />}
            />
            <Route 
              path="/admin/dashboard" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sinastria/:contactId"
              element={
                <ProtectedRoute>
                  <SinastriaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/composto/:contactId"
              element={
                <ProtectedRoute>
                  <RelationshipChartPage kind="composto" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/davison/:contactId"
              element={
                <ProtectedRoute>
                  <RelationshipChartPage kind="davison" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/aurora"
              element={
                <ProtectedRoute>
                  <AuroraQuestionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/horoscope"
              element={
                <ProtectedRoute>
                  <HoroscopePage />
                </ProtectedRoute>
              }
            />

            {/* Retorno do Mercado Pago */}
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/payment/pending" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/payment/failure" element={<Navigate to="/checkout" />} />

            {/* Rota padrão */}
            <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <Footer />
            <InstallPrompt />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
